import React, { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ExternalLink, Send } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { formatMoney, formatDate } from "@/lib/format";
import { StatusChip } from "@/components/common/StatusChip";
import { DataTable } from "@/components/common/DataTable";
import { EmptyState } from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

const ESTADO_TONE = { pendiente: "gray", estimado: "gray", emitida: "blue", enviada: "amber", cobrada: "green", error: "red", estimado_anulado: "gray" };
const BRAND_LABEL = { bb: "B&B", betos: "Betos" };
const PAGE_SIZE = 25;

export const QboInvoicesPanel = () => {
  const { t } = useT();
  const { can } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const canUpdate = can("finance", "update");
  const [estado, setEstado] = useState("all");
  const [brand, setBrand] = useState("all");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState([]);
  const [vista, setVista] = useState(null); // null | "sin_estimado"
  // Fix M (2026-09-28): anular un estimado que no se va a facturar, con motivo.
  const [anular, setAnular] = useState(null);
  const [motivoAnular, setMotivoAnular] = useState("");
  const prevJobStatusRef = useRef(undefined);

  const { data, isLoading } = useQuery({
    queryKey: ["qbo-invoices", estado, brand, page],
    queryFn: async () => (await api.get("/qbo/invoices", {
      params: { estado: estado === "all" ? undefined : estado, brand: brand === "all" ? undefined : brand,
               page, page_size: PAGE_SIZE },
    })).data,
    enabled: vista !== "sin_estimado",
  });
  const { data: sinEstimado, isLoading: loadingSinEstimado } = useQuery({
    queryKey: ["qbo-scheduled-without-estimate"],
    queryFn: async () => (await api.get("/qbo/scheduled-without-estimate")).data,
    enabled: vista === "sin_estimado",
  });
  const items = data?.items || [];
  const total = data?.total || 0;
  const counts = data?.counts || {};
  const totalTodas = Object.values(counts).reduce((a, b) => a + b, 0);

  const { data: jobData } = useQuery({
    queryKey: ["qbo-bulk-issue-status"],
    queryFn: async () => (await api.get("/qbo/invoices/bulk-issue-status")).data,
    refetchInterval: (query) => (query.state.data?.job?.status === "en_curso" ? 2000 : false),
  });
  const job = jobData?.job;
  const isRunning = job?.status === "en_curso";

  useEffect(() => {
    if (!job) return;
    const prev = prevJobStatusRef.current;
    if (prev === "en_curso" && job.status === "completado") {
      const r = job.resultado || {};
      toast.success(t("wo.detail.qbo.bulk_result", { emitidas: r.emitidas, errores: r.errores, omitidas: r.omitidas }));
      setSelected([]);
      qc.invalidateQueries({ queryKey: ["qbo-invoices"] });
    } else if (prev === "en_curso" && job.status === "fallido") {
      toast.error(job.error || t("app.error"));
    }
    prevJobStatusRef.current = job.status;
  }, [job?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const retry = useMutation({
    mutationFn: (woId) => api.post(`/qbo/invoices/${woId}/retry`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["qbo-invoices"] }); toast.success(t("app.saved")); },
    onError: (e) => toast.error(apiError(e, t("app.error"))),
  });
  const bulkIssue = useMutation({
    mutationFn: (ids) => api.post("/qbo/invoices/bulk-issue", { ids }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["qbo-bulk-issue-status"] }),
    onError: (e) => {
      if (e?.response?.status === 409) {
        toast.error(t("wo.detail.qbo.bulk_already_running"));
        qc.invalidateQueries({ queryKey: ["qbo-bulk-issue-status"] });
        return;
      }
      toast.error(apiError(e, t("app.error")));
    },
  });

  const voidEstimate = useMutation({
    mutationFn: ({ woId, motivo }) => api.post(`/qbo/estimates/${woId}/void`, { motivo }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["qbo-invoices"] });
      toast.success(t("wo.detail.qbo.void_estimate_done"));
      setAnular(null);
      setMotivoAnular("");
    },
    onError: (e) => toast.error(apiError(e, t("app.error"))),
  });

  const setEstadoFilter = (v) => { setEstado(v); setPage(1); };

  const columns = [
    {
      key: "sel", label: "", width: 34,
      render: (inv) => (inv.estado !== "emitida" && inv.estado !== "cobrada" && canUpdate ? (
        <Checkbox checked={selected.includes(inv.work_order_id)} onClick={(e) => e.stopPropagation()}
          onCheckedChange={(v) => setSelected((s) => v ? [...s, inv.work_order_id] : s.filter((x) => x !== inv.work_order_id))} />
      ) : null),
    },
    {
      key: "code", label: t("app.order"),
      render: (inv) => (
        <div>
          <span className="font-mono">{inv.work_order_code}</span>
          {inv.qbo_proyecto_nombre && (
            <p className="mt-0.5 max-w-[240px] truncate text-[11px] text-ink-tertiary" title={inv.qbo_proyecto_nombre}>
              {t("wo.detail.qbo.project")}: {inv.qbo_proyecto_nombre}
            </p>
          )}
        </div>
      ),
    },
    { key: "brand", label: t("app.brand"), render: (inv) => <span className="text-ink-secondary">{BRAND_LABEL[inv.brand] || inv.brand}</span> },
    { key: "estado", label: t("app.status"), render: (inv) => <StatusChip tone={ESTADO_TONE[inv.estado]} label={t(`wo.detail.qbo.estados.${inv.estado}`)} /> },
    { key: "numero", label: t("finance.billing.invoice_number"), render: (inv) => <span className="font-mono">{inv.numero || "—"}</span> },
    {
      key: "monto", label: t("wo.price"), align: "right",
      render: (inv) => (
        <div>
          <span>{formatMoney(inv.monto_cents)}</span>
          {inv.estado === "error" && (
            <p className="mt-0.5 max-w-[220px] truncate text-right text-[11px] text-state-critical" title={inv.error}>{inv.error}</p>
          )}
        </div>
      ),
    },
    { key: "fecha_emision", label: t("finance.billing.invoice_date"), render: (inv) => <span className="text-ink-secondary">{inv.fecha_emision ? formatDate(inv.fecha_emision) : "—"}</span> },
    {
      key: "acciones", label: "", align: "right",
      render: (inv) => (
        <div className="flex items-center justify-end gap-2">
          {inv.link && (
            <a href={inv.link} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-brand hover:text-brand-hover">
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          )}
          {canUpdate && inv.qbo_estimate_id && !inv.estimado_anulado
            && ["estimado", "error"].includes(inv.estado) && (
            <Button data-testid={`qbo-void-estimate-${inv.work_order_id}`} size="sm" variant="outline"
              onClick={(e) => { e.stopPropagation(); setAnular(inv); }}
              className="h-7 rounded-control text-[11.5px] text-state-critical">
              {t("wo.detail.qbo.void_estimate")}
            </Button>
          )}
          {inv.estado === "error" && canUpdate && (
            <Button data-testid={`qbo-retry-${inv.work_order_id}`} size="sm" variant="outline"
              onClick={(e) => { e.stopPropagation(); retry.mutate(inv.work_order_id); }} className="h-7 rounded-control text-[11.5px]">
              {t("wo.detail.qbo.retry")}
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div data-testid="qbo-invoices-panel" className="border border-line bg-paper">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-4 py-2.5">
        <p className="text-[13px] font-semibold text-ink">{t("wo.detail.qbo.panel_title")}</p>
        <div className="flex items-center gap-2">
          <Select value={brand} onValueChange={(v) => { setBrand(v); setPage(1); }}>
            <SelectTrigger data-testid="qbo-filter-brand" className="h-8 w-28 rounded-control text-[12px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("app.all")}</SelectItem>
              <SelectItem value="bb">B&amp;B</SelectItem>
              <SelectItem value="betos">Betos</SelectItem>
            </SelectContent>
          </Select>
          {canUpdate && selected.length > 0 && (
            <Button data-testid="qbo-bulk-issue" size="sm" disabled={isRunning || bulkIssue.isPending}
              onClick={() => bulkIssue.mutate(selected)}
              className="h-8 rounded-control bg-brand text-[12px] text-paper hover:bg-brand-hover">
              <Send className="mr-1.5 h-3.5 w-3.5" /> {t("wo.detail.qbo.bulk_issue", { n: selected.length })}
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1.5 border-b border-line px-4 py-2" data-testid="qbo-invoices-status-counts">
        <button data-testid="qbo-count-all" onClick={() => setEstadoFilter("all")}
          className={cn("rounded-control px-2 py-1 text-[11.5px] font-medium",
            vista !== "sin_estimado" && estado === "all" ? "bg-ink text-ink-inverse" : "text-ink-secondary hover:bg-surface-sunken")}>
          {t("app.all")} {totalTodas}
        </button>
        {["estimado", "emitida", "enviada", "cobrada", "error", "pendiente", "estimado_anulado"].map((e) => (
          <button key={e} data-testid={`qbo-count-${e}`} onClick={() => setEstadoFilter(e)}
            className={cn("rounded-control px-2 py-1 text-[11.5px] font-medium",
              vista !== "sin_estimado" && estado === e ? "bg-ink text-ink-inverse" : "text-ink-secondary hover:bg-surface-sunken")}>
            {t(`wo.detail.qbo.estados.${e}`)} {counts[e] || 0}
          </button>
        ))}
        <span className="mx-1 h-4 w-px bg-line" />
        <button data-testid="qbo-view-sin-estimado" onClick={() => setVista(vista === "sin_estimado" ? null : "sin_estimado")}
          className={cn("rounded-control px-2 py-1 text-[11.5px] font-medium",
            vista === "sin_estimado" ? "bg-ink text-ink-inverse" : "text-ink-secondary hover:bg-surface-sunken")}>
          {t("wo.detail.qbo.scheduled_without_estimate")} {sinEstimado?.length ? `(${sinEstimado.length})` : ""}
        </button>
        <button data-testid="qbo-view-sin-enviar" onClick={() => { setVista(null); setEstadoFilter("emitida"); }}
          className="rounded-control px-2 py-1 text-[11.5px] font-medium text-ink-secondary hover:bg-surface-sunken">
          {t("wo.detail.qbo.issued_without_sent")} {counts.emitida || 0}
        </button>
      </div>

      {isRunning && (
        <div className="border-b border-line px-4 py-2.5" data-testid="qbo-bulk-issue-progress">
          <p className="mb-1.5 text-[12px] text-ink-secondary">
            {t("wo.detail.qbo.bulk_in_progress", { pct: job.pct || 0 })}
          </p>
          <Progress value={job.pct || 0} className="h-1.5" />
        </div>
      )}
      {vista === "sin_estimado" ? (
        <div data-testid="qbo-sin-estimado-table">
          {!loadingSinEstimado && !sinEstimado?.length ? (
            <EmptyState testId="qbo-sin-estimado-empty" title={t("wo.detail.qbo.scheduled_without_estimate_empty")} />
          ) : (
            <table className="w-full text-[12.5px]">
              <tbody>
                {(sinEstimado || []).map((w) => (
                  <tr key={w.id} data-testid={`qbo-sin-estimado-row-${w.id}`} className="cursor-pointer border-b border-line hover:bg-surface-sunken"
                    onClick={() => navigate(`/ordenes/${w.id}`)}>
                    <td className="px-4 py-2 font-mono">{w.code}</td>
                    <td className="px-4 py-2">{w.property_nombre}</td>
                    <td className="px-4 py-2 text-ink-secondary">{formatDate(w.fecha_programada)}</td>
                    <td className="px-4 py-2 text-ink-tertiary">{w.motivo}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : (
        <DataTable
          testId="qbo-invoices-table"
          columns={columns}
          rows={items}
          total={total}
          page={page}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
          loading={isLoading}
          empty={<EmptyState testId="qbo-invoices-empty" title={t("wo.detail.qbo.empty_title")} description={t("wo.detail.qbo.empty_desc")} />}
        />
      )}

      <Dialog open={!!anular} onOpenChange={(v) => { if (!v) { setAnular(null); setMotivoAnular(""); } }}>
        <DialogContent data-testid="qbo-void-estimate-dialog" className="max-w-[460px]">
          <DialogHeader>
            <DialogTitle className="text-[16px]">
              {t("wo.detail.qbo.void_estimate_title", { code: anular?.work_order_code || "" })}
            </DialogTitle>
          </DialogHeader>
          <Textarea data-testid="qbo-void-estimate-motivo" value={motivoAnular} rows={3}
            onChange={(e) => setMotivoAnular(e.target.value)}
            placeholder={t("wo.detail.qbo.void_estimate_ph")} className="text-[13px]" />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setAnular(null); setMotivoAnular(""); }} className="h-9 text-[13px]">
              {t("app.cancel")}
            </Button>
            <Button data-testid="qbo-void-estimate-confirm"
              disabled={motivoAnular.trim().length < 5 || voidEstimate.isPending}
              onClick={() => voidEstimate.mutate({ woId: anular.work_order_id, motivo: motivoAnular.trim() })}
              className="h-9 bg-state-critical text-[13px] text-paper hover:opacity-90">
              {t("wo.detail.qbo.void_estimate")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
